/**
 * A deliberately small frontmatter parser.
 *
 * gray-matter is the obvious reach here, but it pulls js-yaml and a Buffer
 * polyfill into a browser bundle to read seven fixed fields. The schema is
 * closed and validated below, so a ~40 line parser covering the subset we
 * actually author is the better trade.
 *
 * Supported: `key: value` and `key: [a, b, c]`. Values may contain colons.
 * Quotes around a value are optional and stripped.
 */

export interface RawFrontmatter {
  [key: string]: string | string[];
}

export interface ParsedDocument {
  data: RawFrontmatter;
  body: string;
}

const FENCE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

function stripQuotes(value: string): string {
  const trimmed = value.trim();
  const quoted =
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"));
  return quoted ? trimmed.slice(1, -1) : trimmed;
}

export function parseFrontmatter(source: string): ParsedDocument {
  const match = FENCE.exec(source);
  if (!match) {
    return { data: {}, body: source.trim() };
  }

  const data: RawFrontmatter = {};

  for (const line of match[1].split(/\r?\n/)) {
    if (!line.trim() || line.trimStart().startsWith("#")) continue;

    const separator = line.indexOf(":");
    if (separator === -1) continue;

    const key = line.slice(0, separator).trim();
    const rawValue = line.slice(separator + 1).trim();

    if (rawValue.startsWith("[") && rawValue.endsWith("]")) {
      data[key] = rawValue
        .slice(1, -1)
        .split(",")
        .map(stripQuotes)
        .filter(Boolean);
    } else {
      data[key] = stripQuotes(rawValue);
    }
  }

  return { data, body: source.slice(match[0].length).trim() };
}

export function requireString(
  data: RawFrontmatter,
  key: string,
  file: string
): string {
  const value = data[key];
  if (typeof value !== "string" || !value) {
    throw new Error(`Help article ${file}: frontmatter "${key}" is required.`);
  }
  return value;
}

export function requireStringArray(
  data: RawFrontmatter,
  key: string,
  file: string
): string[] {
  const value = data[key];
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(
      `Help article ${file}: frontmatter "${key}" must be a non-empty list.`
    );
  }
  return value;
}
